// The report's two figures, drawn natively from their data (figures/layout.json
// and figures/quadrant.json, via content.json) so they follow the app's theme.
//
// Encoding, validated with the dataviz palette checks in both themes:
//   - proposals: one amber star each, identified by a direct name label (ink,
//     not color), so four proposals never need four competing hues;
//   - research areas joined to a proposal: one green, with the area's kind
//     carried by shape (circle / square / triangle / diamond);
//   - everything else: the neutral --chart-muted.
// With `focusId` set (a proposal's own page) that proposal is emphasized and
// the others recede to gray.
import React, { useEffect, useMemo, useRef, useState } from "react";

export const AREA_KINDS = [
    { key: "experimental", label: "Experimental methods", shape: "circle" },
    { key: "computational", label: "Computational methods", shape: "square" },
    { key: "problems", label: "Research problems", shape: "triangle" },
    { key: "impact", label: "Impact and outcomes", shape: "diamond" },
];
const KIND = Object.fromEntries(AREA_KINDS.map((k) => [k.key, k]));

const QUADRANT_LABELS = {
    frontier: { long: "Sparse and growing: frontier", short: "Frontier" },
    competitive: { long: "Crowded and growing: competitive", short: "Competitive" },
    isolated: { long: "Sparse and flat: isolated, worth asking why", short: "Isolated" },
    mature: { long: "Crowded and flat: mature", short: "Mature" },
};

function useWidth(ref) {
    const [width, setWidth] = useState(0);
    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const observer = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)));
        observer.observe(el);
        return () => observer.disconnect();
    }, [ref]);
    return width;
}

export function shapePath(shape, cx, cy, r) {
    switch (shape) {
        case "square": {
            const s = r * 0.9;
            return `M${cx - s},${cy - s}H${cx + s}V${cy + s}H${cx - s}Z`;
        }
        case "triangle": {
            const s = r * 1.2;
            return `M${cx},${cy - s}L${cx + s * 0.95},${cy + s * 0.65}H${cx - s * 0.95}Z`;
        }
        case "diamond": {
            const s = r * 1.2;
            return `M${cx},${cy - s}L${cx + s},${cy}L${cx},${cy + s}L${cx - s},${cy}Z`;
        }
        case "star": {
            let d = "";
            for (let i = 0; i < 10; i++) {
                const rad = i % 2 === 0 ? r : r * 0.45;
                const a = (Math.PI / 5) * i - Math.PI / 2;
                d += `${i === 0 ? "M" : "L"}${cx + rad * Math.cos(a)},${cy + rad * Math.sin(a)}`;
            }
            return `${d}Z`;
        }
        default:
            return `M${cx - r},${cy}a${r},${r} 0 1,0 ${2 * r},0a${r},${r} 0 1,0 ${-2 * r},0`;
    }
}

function Tooltip({ tip, width }) {
    if (!tip) return null;
    const flip = tip.x > width - 260;
    return (
        <div
            className="cfri-tooltip"
            role="status"
            style={{ top: tip.y + 14, left: flip ? undefined : tip.x + 14, right: flip ? width - tip.x + 14 : undefined }}
        >
            {tip.rows.map((row, i) => (
                <div key={i} className={i === 0 ? "cfri-tooltip-lead" : "cfri-tooltip-row"}>{row}</div>
            ))}
        </div>
    );
}

function nearest(items, px, py, reach) {
    let best = null;
    let bestD = Infinity;
    for (const it of items) {
        const d = Math.hypot(it.cx - px, it.cy - py);
        if (d <= Math.max(reach, it.r + 6) && d < bestD) {
            best = it;
            bestD = d;
        }
    }
    return best;
}

function pointerIn(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    return [e.clientX - rect.left, e.clientY - rect.top];
}

// Where each shown proposal's name goes: above its star unless that overlaps
// a name already placed, another star or the chart's edge; then below, right,
// left. Close stars on a narrow chart would otherwise print over each other.
// Widths are estimated (the 12.5px semibold label runs about 7.3px a letter).
function placeLabels(stars, shown, r, width) {
    const placed = [];
    const at = {};
    for (const s of stars.filter(shown)) {
        const w = s.name.length * 7.3;
        const box = ({ x, y, anchor }) => {
            const x0 = anchor === "middle" ? x - w / 2 : anchor === "start" ? x : x - w;
            return { x0, x1: x0 + w, y0: y - 11, y1: y + 3 };
        };
        const clear = (b) => b.x0 >= 0 && b.x1 <= width
            && !placed.some((o) => b.x0 < o.x1 && b.x1 > o.x0 && b.y0 < o.y1 && b.y1 > o.y0)
            && !stars.some((o) => o !== s && b.x0 < o.cx + r && b.x1 > o.cx - r && b.y0 < o.cy + r && b.y1 > o.cy - r);
        const options = [
            { x: s.cx, y: s.cy - r - 6, anchor: "middle" },
            { x: s.cx, y: s.cy + r + 14, anchor: "middle" },
            { x: s.cx + r + 4, y: s.cy + 4, anchor: "start" },
            { x: s.cx - r - 4, y: s.cy + 4, anchor: "end" },
        ];
        at[s.id] = options.find((o) => clear(box(o))) || options[0];
        placed.push(box(at[s.id]));
    }
    return at;
}

// A star that can be hovered, focused and (when it leads somewhere) opened
function ProposalStar({ star, r, dimmed, labelAt, onSelect, onFocusTip, onBlurTip }) {
    const selectable = !!onSelect;
    return (
        <g
            className={`cfri-star${dimmed ? " cfri-star-dim" : ""}`}
            tabIndex={0}
            role={selectable ? "link" : "img"}
            aria-label={`${star.name}${selectable ? ", open proposal" : ""}`}
            onFocus={() => onFocusTip(star)}
            onBlur={onBlurTip}
            onKeyDown={(e) => {
                if (selectable && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    onSelect(star.id);
                }
            }}
            style={{ cursor: selectable ? "pointer" : "default" }}
        >
            <path d={shapePath("star", star.cx, star.cy, r)} className="cfri-star-mark" />
            {labelAt && (
                <text x={labelAt.x} y={labelAt.y} textAnchor={labelAt.anchor} className="cfri-star-label">{star.name}</text>
            )}
        </g>
    );
}

// --- Landscape: the 497 research areas, with the proposals placed on them ----

export function LandscapeChart({ data, focusId, onSelectProposal, compact = false }) {
    const ref = useRef(null);
    const width = useWidth(ref);
    const [tip, setTip] = useState(null);
    const [hoverId, setHoverId] = useState(null);

    const layout = useMemo(() => {
        if (!width) return null;
        const xs = data.areas.map((a) => a[0]).concat(data.proposals.map((p) => p.x));
        const ys = data.areas.map((a) => a[1]).concat(data.proposals.map((p) => p.y));
        const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
        const pad = { l: 14, r: 14, t: compact ? 22 : 30, b: 14 };
        // Each axis fills the frame, as in the report's own figure (and its PDF)
        const height = Math.round(Math.min(Math.max(width * 0.6, compact ? 240 : 320), compact ? 340 : 640));
        const sx = (v) => pad.l + ((v - x0) / (x1 - x0)) * (width - pad.l - pad.r);
        const sy = (v) => pad.t + ((y1 - v) / (y1 - y0)) * (height - pad.t - pad.b);
        const maxN = Math.max(...data.areas.map((a) => a[2]));
        const k = Math.min(Math.max(width / 900, 0.6), 1.15);
        const areas = data.areas.map(([x, y, n, kind, title], index) => ({
            index, n, kind, title, cx: sx(x), cy: sy(y), r: (2.2 + 6 * Math.sqrt(n / maxN)) * k,
        }));
        const stars = data.proposals.map((p) => ({ ...p, cx: sx(p.x), cy: sy(p.y), r: 15 }));
        return { height, areas, stars, starR: compact ? 8 : 10 * Math.max(k, 0.8) };
    }, [data, width, compact]);

    const shown = focusId ? (layout?.stars ?? []).filter((s) => s.id === focusId) : layout?.stars ?? [];
    const linked = new Map();
    shown.forEach((s) => s.near.forEach((i) => linked.set(i, true)));

    const areaTip = (a) => ({
        x: a.cx, y: a.cy,
        rows: [a.title, `${(KIND[a.kind] || {}).label || a.kind} · ${a.n.toLocaleString("en-US")} papers`],
    });
    const starTip = (s) => ({
        x: s.cx, y: s.cy,
        rows: [s.name, `Joined to the ${s.near.length} research areas its text sits closest to`, ...(onSelectProposal && s.id !== focusId ? ["Click to open the proposal"] : [])],
    });

    const onMove = (e) => {
        if (!layout) return;
        const [px, py] = pointerIn(e);
        const star = nearest(layout.stars, px, py, 18);
        if (star) {
            setHoverId(star.id);
            setTip(starTip(star));
            return;
        }
        setHoverId(null);
        const area = nearest(layout.areas, px, py, 10);
        setTip(area ? areaTip(area) : null);
    };

    const select = onSelectProposal ? (id) => id !== focusId && onSelectProposal(id) : null;
    const labels = layout
        ? placeLabels(layout.stars, (s) => !compact || !focusId || s.id === focusId, layout.starR, width)
        : {};

    return (
        <div className="cfri-figure" ref={ref}>
            {layout && (
                <>
                    <svg
                        width={width}
                        height={layout.height}
                        role="img"
                        aria-label="Map of the research areas of the cystic fibrosis literature, with the proposals placed on it"
                        onPointerMove={onMove}
                        onPointerLeave={() => { setTip(null); setHoverId(null); }}
                        onClick={() => hoverId && select && select(hoverId)}
                        style={{ cursor: hoverId && select && hoverId !== focusId ? "pointer" : "default" }}
                    >
                        {layout.areas.filter((a) => !linked.has(a.index)).map((a) => (
                            <circle key={a.index} cx={a.cx} cy={a.cy} r={a.r} className="cfri-area-bg" />
                        ))}
                        {shown.map((s) =>
                            s.near.map((i) => {
                                const a = layout.areas[i];
                                const faded = hoverId && hoverId !== s.id;
                                return (
                                    <line key={`${s.id}-${i}`} x1={s.cx} y1={s.cy} x2={a.cx} y2={a.cy}
                                          className="cfri-link" style={{ opacity: faded ? 0.12 : undefined }} />
                                );
                            })
                        )}
                        {layout.areas.filter((a) => linked.has(a.index)).map((a) => (
                            <path key={a.index} d={shapePath((KIND[a.kind] || {}).shape, a.cx, a.cy, Math.max(a.r, 4))} className="cfri-area-linked" />
                        ))}
                        {layout.stars.map((s) => (
                            <ProposalStar
                                key={s.id}
                                star={s}
                                r={layout.starR}
                                dimmed={!!focusId && s.id !== focusId}
                                labelAt={labels[s.id]}
                                onSelect={select}
                                onFocusTip={(star) => setTip(starTip(star))}
                                onBlurTip={() => setTip(null)}
                            />
                        ))}
                    </svg>
                    <Tooltip tip={tip} width={width} />
                </>
            )}
            <div className="cfri-legend">
                {AREA_KINDS.map((k) => (
                    <span key={k.key} className="cfri-legend-item">
                        <svg width="14" height="14" aria-hidden="true"><path d={shapePath(k.shape, 7, 7, 5)} className="cfri-area-linked" /></svg>
                        {k.label}
                    </span>
                ))}
                <span className="cfri-legend-item">
                    <svg width="14" height="14" aria-hidden="true"><circle cx="7" cy="7" r="5" className="cfri-area-bg" /></svg>
                    Other CF research areas
                </span>
                <span className="cfri-legend-item">
                    <svg width="16" height="16" aria-hidden="true"><path d={shapePath("star", 8, 8, 7)} className="cfri-star-mark" /></svg>
                    Proposal
                </span>
            </div>
        </div>
    );
}

// --- Quadrant: how crowded each proposal's corner is, and how fast it grows ---

const X_TICKS = [3, 10, 30, 100, 300, 1000, 3000];
const Y_TICKS = [[-2, "¼×"], [-1, "½×"], [0, "same"], [1, "2×"], [2, "4×"]];
const Y_LIMIT = 2.2;

export function QuadrantChart({ data, focusId, onSelectProposal, compact = false }) {
    const ref = useRef(null);
    const width = useWidth(ref);
    const [tip, setTip] = useState(null);
    const [hoverId, setHoverId] = useState(null);

    const layout = useMemo(() => {
        if (!width) return null;
        const narrow = compact || width < 560;
        const pad = { l: narrow ? 52 : 58, r: 12, t: 12, b: narrow ? 38 : 46 };
        const height = Math.round(Math.min(Math.max(width * (narrow ? 0.85 : 0.56), 260), 520));
        const xs = data.points.map((p) => p[0]).concat(data.proposals.map((p) => p.x));
        const x0 = Math.min(...xs) - 0.05;
        const x1 = Math.max(...xs) + 0.05;
        const sx = (v) => pad.l + ((v - x0) / (x1 - x0)) * (width - pad.l - pad.r);
        const sy = (v) => pad.t + ((Y_LIMIT - v) / (2 * Y_LIMIT)) * (height - pad.t - pad.b);
        return {
            narrow, pad, height, sx, sy,
            points: data.points.filter(([, y]) => Math.abs(y) <= Y_LIMIT).map(([x, y]) => [sx(x), sy(y)]),
            xTicks: X_TICKS.map((n) => [n, Math.log10(1 + n)]).filter(([, v]) => v >= x0 && v <= x1),
            stars: data.proposals.map((p) => ({ ...p, cx: sx(p.x), cy: sy(p.y), r: 15 })),
        };
    }, [data, width, compact]);

    const starTip = (s) => ({
        x: s.cx, y: s.cy,
        rows: [
            s.name,
            s.label,
            `${s.papersClose.toLocaleString("en-US")} CF papers close to it`,
            `${s.early} papers in 2016–20 → ${s.late} in 2021–25 (×${(2 ** s.y).toFixed(2)} vs CF ×${data.fieldGrowth})`,
            ...(onSelectProposal && s.id !== focusId ? ["Click to open the proposal"] : []),
        ],
    });

    const onMove = (e) => {
        if (!layout) return;
        const [px, py] = pointerIn(e);
        const star = nearest(layout.stars, px, py, 18);
        setHoverId(star ? star.id : null);
        setTip(star ? starTip(star) : null);
    };

    const select = onSelectProposal ? (id) => id !== focusId && onSelectProposal(id) : null;

    if (!layout) return <div className="cfri-figure" ref={ref} style={{ minHeight: 260 }} />;

    const { pad, height, sx, sy, narrow } = layout;
    const starR = compact ? 8 : 10;
    const labels = placeLabels(layout.stars, (s) => !focusId || s.id === focusId || !compact, starR, width);
    const mx = sx(data.medians.x);
    const my = sy(data.medians.y);
    const corner = (key) => (narrow ? QUADRANT_LABELS[key].short : QUADRANT_LABELS[key].long);
    const right = width - pad.r - 6;
    const bottom = height - pad.b;

    return (
        <div className="cfri-figure" ref={ref}>
            <svg
                width={width}
                height={height}
                role="img"
                aria-label="Chart: how crowded and how fast-growing the corner of CF research around each proposal is"
                onPointerMove={onMove}
                onPointerLeave={() => { setTip(null); setHoverId(null); }}
                onClick={() => hoverId && select && select(hoverId)}
                style={{ cursor: hoverId && select && hoverId !== focusId ? "pointer" : "default" }}
            >
                {Y_TICKS.map(([v, label]) => (
                    <g key={v}>
                        <line x1={pad.l} x2={width - pad.r} y1={sy(v)} y2={sy(v)} className="cfri-grid" />
                        <text x={pad.l - 6} y={sy(v) + 4} textAnchor="end" className="cfri-tick">{label}</text>
                    </g>
                ))}
                {layout.xTicks.map(([n, v]) => (
                    <g key={n}>
                        <line x1={sx(v)} x2={sx(v)} y1={pad.t} y2={bottom} className="cfri-grid" />
                        <text x={sx(v)} y={bottom + 15} textAnchor="middle" className="cfri-tick">
                            {narrow && n >= 1000 ? `${n / 1000}K` : n.toLocaleString("en-US")}
                        </text>
                    </g>
                ))}
                <text x={pad.l + (width - pad.l - pad.r) / 2} y={height - 6} textAnchor="middle" className="cfri-axis-title">
                    {narrow ? "CF papers close to it (log scale)" : "How crowded: CF papers close to it (log scale)"}
                </text>
                <text transform={`translate(12 ${pad.t + (bottom - pad.t) / 2}) rotate(-90)`} textAnchor="middle" className="cfri-axis-title">
                    {narrow ? "Growth vs CF" : "Growth vs CF as a whole (2021–25 vs 2016–20)"}
                </text>

                {layout.points.map(([x, y], i) => <circle key={i} cx={x} cy={y} r={1.6} className="cfri-point" />)}

                <line x1={mx} x2={mx} y1={pad.t} y2={bottom} className="cfri-median" />
                <line x1={pad.l} x2={width - pad.r} y1={my} y2={my} className="cfri-median" />
                {!narrow && (
                    <>
                        <text x={mx + 6} y={pad.t + 32} className="cfri-median-label">CF median: {fmtNumber(data.medianPapers)} papers</text>
                        <text x={pad.l + 6} y={my - 6} className="cfri-median-label">CF median growth: ×{data.fieldGrowth}</text>
                    </>
                )}

                <text x={pad.l + 6} y={pad.t + 14} className="cfri-corner">{corner("frontier")}</text>
                <text x={right} y={pad.t + 14} textAnchor="end" className="cfri-corner">{corner("competitive")}</text>
                <text x={pad.l + 6} y={bottom - 8} className="cfri-corner">{corner("isolated")}</text>
                <text x={right} y={bottom - 8} textAnchor="end" className="cfri-corner">{corner("mature")}</text>

                {layout.stars.map((s) => (
                    <ProposalStar
                        key={s.id}
                        star={s}
                        r={starR}
                        dimmed={!!focusId && s.id !== focusId}
                        labelAt={labels[s.id]}
                        onSelect={select}
                        onFocusTip={(star) => setTip(starTip(star))}
                        onBlurTip={() => setTip(null)}
                    />
                ))}
            </svg>
            <Tooltip tip={tip} width={width} />
            <div className="cfri-legend">
                <span className="cfri-legend-item">
                    <svg width="16" height="16" aria-hidden="true"><path d={shapePath("star", 8, 8, 7)} className="cfri-star-mark" /></svg>
                    Proposal
                </span>
                <span className="cfri-legend-item">
                    <svg width="10" height="10" aria-hidden="true"><circle cx="5" cy="5" r="3" className="cfri-point" style={{ opacity: 1 }} /></svg>
                    {data.points.length.toLocaleString("en-US")} CF papers placed the same way
                </span>
                <span className="cfri-legend-item">
                    <svg width="18" height="10" aria-hidden="true"><line x1="0" x2="18" y1="5" y2="5" className="cfri-median" /></svg>
                    CF medians
                </span>
            </div>
        </div>
    );
}

function fmtNumber(n) {
    return Number.isInteger(n) ? n.toLocaleString("en-US") : Math.round(n).toLocaleString("en-US");
}
